import type { ReactNode } from 'react';
import { AppBar, Body, Screen, SiteFooter } from './ui';

/**
 * 規約類3ページ（/terms · /privacy · /contact）の共通の枠。
 *
 * 中身は静的なテキストだけで、state も通信も持たない。3ページで見出しの体裁が
 * ずれると読み手には別のサイトに見えるので、枠と見出しはここに集約する。
 *
 * NoIndexRoutes には入れない。/g/ 配下と違い、この3ページはクロールされてほしい。
 */
export function LegalScreen({
  title,
  updatedAt,
  children,
}: {
  title: string;
  /** 表示するのは規約とポリシーだけ。問い合わせページには改定の概念がない */
  updatedAt?: string;
  children: ReactNode;
}) {
  return (
    <Screen>
      <AppBar title={title} backTo="/" />
      {/*
        gap は Article 側の mt で作るので殺しておく。両方効かせると条文の間隔が
        二重になる。pb-12 はフッターとの間隔で、トップと揃えた 48px。
      */}
      <Body className="gap-0 pb-12">
        {updatedAt && <div className="mb-4 text-[11.5px] text-ink-faint">最終更新日：{updatedAt}</div>}
        {children}
      </Body>
      <SiteFooter />
    </Screen>
  );
}

/**
 * 条文ひとつ。
 *
 * 番号は「第n条」と書かずに数字だけにしている。本文から参照できれば十分で、
 * 画面の幅に対して「第」と「条」は場所を取るだけになる。問い合わせページのように
 * 番号が要らないところは no を省く。
 */
export function Article({
  no,
  title,
  children,
}: {
  no?: number;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="mt-5 first:mt-0">
      <h2 className="flex gap-2 text-[15px] font-bold text-felt">
        {no !== undefined && (
          <span className="shrink-0 text-[13px] font-semibold tabular-nums text-ink-soft">{no}</span>
        )}
        {title}
      </h2>
      <div className="mt-1.5 flex flex-col gap-2.5 text-[13.5px] leading-[1.8]">{children}</div>
    </section>
  );
}

/** 条文の中の箇条書き。gap ではなく行間で詰めたいので ul 側で持つ */
export function Points({ items }: { items: string[] }) {
  return (
    <ul className="list-disc pl-5">
      {items.map((t) => (
        <li key={t}>{t}</li>
      ))}
    </ul>
  );
}
